// observations — the SHARED render half of the observation grammar (ADR-061 §5).
//
// The vocabulary half lives in `packages/schemas/src/observations/registry.ts`,
// shared with the backend. This file owns how a observation LOOKS, and nothing
// else: no comparator lives here, and none ever should — comparators stay in
// the module that owns them, and nothing may call one it does not own.
//
// The two axes meet on one small element:
//   · the GLYPH is the KIND   — the same four shapes on every register
//   · COLOUR AND WEIGHT are the IMPACT — filled · tinted · outline
// Weight is not decoration. Impact carried by hue alone fails colour-blind
// readers, and impact is the axis that cannot be lost.
//
// COLOUR ALWAYS MEANS IMPACT. So where no single impact applies — a kind chip
// spans all three at once — the glyph runs NEUTRAL rather than picking one,
// because colouring it would assert something untrue.

import type { ReactNode } from 'react';

import { icons, type Icon as IconType } from '../ui/icons.js';
import {
  OBSERVATION_KINDS,
  familiesOf,
  type ObservationFamily,
  type ObservationImpact,
  type ObservationKind,
  type ObservationSurface,
} from '@ems-app/schemas/observations/registry';

/** Concept → glyph, by kind. The whole learning cost of the system, and it
 *  never grows: a new family reuses one of these four. */
export const KIND_ICON: Record<ObservationKind, IconType> = {
  missing: icons.observationMissing,
  conflict: icons.observationConflict,
  unverified: icons.observationUnverified,
  breach: icons.observationBreach,
};

// One coherent pill set (T elegance round, architect-tuned live): high and
// medium wear EXACTLY the Badge subtle recipe in their hue (/15 tint · /40
// border · primary ink — the Lost chip's softness; a full-strength border and
// danger-red ink read harsher than the badge beside them), low stays the
// neutral outline. The icon is UNFILLED at every impact. The weight axis is
// deliberately soft here — hue carries impact on the ROW; the peek's cards
// keep the full impact anatomy.
const IMPACT_MARK: Record<ObservationImpact, string> = {
  high: 'bg-[var(--color-danger)]/15 border-[var(--color-danger)]/40 text-[var(--color-text-primary)]',
  medium: 'bg-[var(--color-warning)]/15 border-[var(--color-warning)]/40 text-[var(--color-text-primary)]',
  low: 'border-[var(--color-border-strong)] bg-transparent text-[var(--color-text-secondary)]',
};
const NEUTRAL_MARK = 'border-transparent bg-transparent text-[var(--color-text-secondary)]';

export const IMPACT_CARD: Record<ObservationImpact, string> = {
  high: 'border-[var(--color-danger)]/45 bg-[var(--color-danger)]/8',
  medium: 'border-[var(--color-warning)]/45 bg-[var(--color-warning)]/8',
  low: 'border-[var(--color-border)] bg-[var(--color-bg-surface-alt)]/50',
};

/** The impact scale as TEXT tone — for an in-place gap word or count where a
 *  mark doesn't fit (a table cell's "Missing", a section's missing-count).
 *  One scale everywhere: a surface deriving its own gap colours is the private
 *  law ADR-061 dissolved. */
export const IMPACT_TEXT: Record<ObservationImpact, string> = {
  high: 'text-[var(--color-danger)]',
  medium: 'text-[var(--color-warning)]',
  low: 'text-[var(--color-text-secondary)]',
};

export interface ObservationMarkProps {
  kind: ObservationKind;
  /** Omit for the NEUTRAL rendering — a kind chip or legend, where no single
   *  impact applies and colouring would lie. */
  impact?: ObservationImpact;
  /** Shown when a subject carries more than one observation; the mark itself never
   *  grows, so three observations read the same size as one. */
  count?: number;
  /** The phone rendering: the column never drops, it degrades to this — so the
   *  mark never changes address. */
  bare?: boolean;
  className?: string;
}

export function ObservationMark({ kind, impact, count = 1, bare = false, className = '' }: ObservationMarkProps) {
  const Icon = KIND_ICON[kind];
  const tone = impact ? IMPACT_MARK[impact] : NEUTRAL_MARK;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border text-xs font-semibold tabular-nums ${tone} ${
        bare ? 'p-1' : 'px-1.5 py-px'
      } ${className}`}
    >
      <Icon size={13} aria-hidden />
      {/* The number ALWAYS shows on the row face (T ruling — a countless pill
          and a counted one read as different species); only `bare` (the
          ultra-tight degraded face + card headers) drops it. */}
      {!bare && count}
    </span>
  );
}

export interface KindMarkSummary {
  kind: ObservationKind;
  impact: ObservationImpact;
  count: number;
}

/** Fold a row's (worst-first) findings into the ROW face's per-kind summaries:
 *  one mark per kind present, that kind's WORST impact, that kind's OWN count.
 *  A per-kind count can never lie about the mix — the concern that once banned
 *  counts on row marks was a TOTAL riding the worst glyph. */
export function kindMarkSummaries(found: { kind: ObservationKind; impact: ObservationImpact }[]): KindMarkSummary[] {
  const out: KindMarkSummary[] = [];
  for (const f of found) {
    const hit = out.find((m) => m.kind === f.kind);
    if (hit) hit.count += 1;
    else out.push({ kind: f.kind, impact: f.impact, count: 1 });
  }
  return out;
}

/** The ROW face of the grammar (ADR-061 §5, re-cut at T): counted per-kind
 *  marks and NO words in the cell — the full sentence rides `title` + an
 *  sr-only span (the peek is the detail surface; a tab-stop tooltip would
 *  bloat the keyboard path). One geometry whatever the mix; the column's floor
 *  collapses from a text budget to a marks budget — the FR-cropping fix, and
 *  the same face on every register (the shared primitive law). */
export function ObservationMarkRow({ marks, label, className = '' }: { marks: KindMarkSummary[]; label: string; className?: string }) {
  if (marks.length === 0) return null;
  // `title` rides a FULL-WIDTH flex wrapper, not the pills — a mark-sized
  // hover target was practically unfindable (the architect's live report);
  // hovering anywhere in the cell now reveals the sentence.
  return (
    <span className={`flex min-h-6 w-full items-center gap-1 ${className}`} title={label}>
      {marks.map((m) => (
        <ObservationMark key={m.kind} kind={m.kind} impact={m.impact} count={m.count} />
      ))}
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** `[{ kind, families }]` for one surface — drives the catalogue page AND each
 *  surface's filter options. DERIVED from the registry: a family added there
 *  appears here, and in the catalogue, with no edit. Empty kinds drop out on
 *  their own (a register never raises a breach). */
export function observationGroups(surface: ObservationSurface): { kind: ObservationKind; families: ObservationFamily[] }[] {
  return OBSERVATION_KINDS.map((kind) => ({ kind, families: familiesOf(surface, kind) })).filter(
    (g) => g.families.length > 0,
  );
}

// ── THE PEEK'S OBSERVATION CARD (ADR-061 §5) ─────────────────────────────────
// One card per observation, worst first, its SHAPE decided by kind — the ONE
// peek grammar every register speaks (judged on the asset register, adopted by
// sites). The card owns geometry, tint, and the kind header; every WORD arrives
// localized from the caller, and the ACTION ROW is per-surface — constrained by
// the family's permitted reactions (nothing may render a button the family does
// not permit: missing data is never dismissible — fill it). What stays
// per-surface is only what the domain forces: an asset field is a value (fill it
// inline), a solution value is a DATED PERIOD (sites keep their
// changed-vs-corrected fork).

/** A conflict's two sides — the same shape whichever two records disagree. */
export interface ComparePairProps {
  declaredLabel: string;
  declared: ReactNode;
  foundLabel: string;
  found: ReactNode;
  /** The comparator word between the sides (localized by the caller). */
  vsLabel: string;
  /** Provenance under the found side — "source · date", composed by the caller. */
  provenance?: string;
}

export function ComparePair({ declaredLabel, declared, foundLabel, found, vsLabel, provenance }: ComparePairProps) {
  return (
    <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
      <div className="min-w-0 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-disabled)]">{declaredLabel}</div>
        <div className="truncate text-xs font-medium" dir="ltr">{declared}</div>
      </div>
      <span aria-hidden className="self-center text-xs text-[var(--color-text-disabled)]">{vsLabel}</span>
      <div className="min-w-0 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-disabled)]">{foundLabel}</div>
        <div className="truncate text-xs font-medium" dir="ltr">{found}</div>
        {provenance && <div className="truncate text-[10px] text-[var(--color-text-disabled)]">{provenance}</div>}
      </div>
    </div>
  );
}

export interface ObservationCardProps {
  kind: ObservationKind;
  impact: ObservationImpact;
  /** The kind word the header prints — localized by the caller
   *  (`observations.kind.*`); NEVER the family key (model vocabulary). */
  kindLabel: string;
  /** WHAT is wrong — the subject field's label, or the family's word. */
  /** The finding — a line, or a structure such as the file peek's classification rows. */
  subject: ReactNode;
  /** Conflict only — both sides with provenance. */
  compare?: ComparePairProps;
  /** The muted, restorable face (ADR-061 §3 — a dismissed observation stays
   *  visible at the foot of the same list, never dismiss-and-forget). */
  dismissed?: { line: string; action?: ReactNode };
  /** The live face's action row — per-surface, per the family's permitted
   *  reactions. */
  actions?: ReactNode;
}

export function ObservationCard({ kind, impact, kindLabel, subject, compare, dismissed, actions }: ObservationCardProps) {
  return (
    <div className={`rounded-lg border p-3 ${dismissed ? 'border-[var(--color-border)] bg-transparent opacity-70' : IMPACT_CARD[impact]}`}>
      <div className="flex items-center gap-2">
        <ObservationMark kind={kind} impact={impact} bare />
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">{kindLabel}</span>
      </div>
      <div className="mt-1.5 text-sm font-medium text-[var(--color-text-primary)]">{subject}</div>
      {compare && <ComparePair {...compare} />}
      {dismissed ? (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-[var(--color-text-secondary)]">{dismissed.line}</span>
          {dismissed.action && <span className="ms-auto shrink-0">{dismissed.action}</span>}
        </div>
      ) : (
        actions && <div className="mt-2.5 flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}
